/**
 * El módulo de control de horas.
 *
 * ── DOS TÉRMINOS SE QUEDAN EN INGLÉS, Y ES A PROPÓSITO ────────────────────────────────────────────
 *
 * `Service Tickets` and the word "ticket" in `ticketCount` are NOT translated, and this is the
 * judgement the brief asked for rather than a gap. Both name specific BILLING artifacts in this
 * company's workflow, and this application carries two of them that are not the same document:
 * a FIELD ticket (T&M work, billed through invoice_packages) and a SERVICE ticket (a service call).
 * Spanish offers `boleta`, `vale`, `orden de servicio` and `ticket` for both, and every one of them
 * collapses a distinction the office depends on when it invoices. A foreman sent to the wrong screen
 * by a guessed word costs somebody money; an English label he can read out to the office does not.
 * The English shows through because a missing translation shows the base language — the same rule
 * that makes every other partial screen safe.
 *
 * ── LO QUE SÍ SE TRADUJO, Y CON QUÉ PALABRA ──────────────────────────────────────────────────────
 *
 * `cuadrilla` for crew — the word on a Mexican or Central American job site. `equipo` would collide
 * with `Equipo` = equipment, which the QR page already uses in the field.
 *
 * `supervisor` for foreman, taken from the QR page (`Avise a su supervisor`) rather than re-decided.
 * `mayordomo` is the traditional Mexican construction word and would have been defensible on its own,
 * but a person cannot be called two things in one application.
 *
 * `Revisado` for `timekeeper_reviewed` and `Aprobado` for `approved` are two DIFFERENT states and the
 * Spanish keeps them as far apart as the English does: revisado = somebody looked at it, aprobado =
 * it is going to payroll. Collapsing them would tell a foreman his crew is paid when it is not.
 */
export default {
  // ── la barra del módulo ───────────────────────────────────────────────────────────────────────
  navDashboard: 'Panel',
  navDailyReports: 'Reportes diarios',
  navServiceTickets: 'Service Tickets',
  navMyHours: 'Mis horas',
  navMyHoursCrew: 'Mis horas / Cuadrilla',
  navMasterTimesheet: 'Hoja de horas general',
  navCrewView: 'Vista de la cuadrilla',
  navManageCrews: 'Administrar cuadrillas',
  navSites: 'Sitios',
  navAssignments: 'Asignaciones',

  // ── estado del reporte ────────────────────────────────────────────────────────────────────────
  statusDraft: 'Borrador',
  statusSubmitted: 'Enviado',
  statusTimekeeperReviewed: 'Revisado',
  statusApproved: 'Aprobado',
  statusRejected: 'Rechazado',

  // ── panel ─────────────────────────────────────────────────────────────────────────────────────
  dashboardTitle: 'Panel de control de horas',
  periodAll: 'Todo',
  periodYear: 'Año',
  periodMonth: 'Mes',
  periodWeek: 'Semana',
  periodCustom: 'Otras fechas',
  dateRangeTo: 'a',
  newReport: 'Nuevo reporte',
  weekOf: (range) => `Semana del ${range}`,
  yearOf: (year) => `Año ${year}`,
  needsAttention: (n) => (n === 1
    ? '1 reporte necesita atención'
    : `${n} reportes necesitan atención`),
  attentionAwaitingApproval: (waiting, rejected) => `${waiting} esperando aprobación, ${rejected} rechazados`,
  attentionSubmitted: (submitted, rejected) => `${submitted} enviados, ${rejected} rechazados`,
  review: 'Revisar',
  totalReports: 'Total de reportes',
  employeeEntries: 'Registros de empleados',
  laborHours: 'Horas de mano de obra',
  equipmentHours: 'Horas de equipo',
  recentReports: 'Reportes recientes',
  viewAll: 'Ver todos',
  noReportsForPeriod: 'No hay reportes en este periodo',
  activeCrews: 'Cuadrillas activas',
  manage: 'Administrar',
  noActiveCrews: 'No hay cuadrillas activas',
  noForeman: 'Sin supervisor',
  crewLine: (name, members) => (members === 1
    ? `${name} · 1 integrante`
    : `${name} · ${members} integrantes`),
  reportLine: (employees, hours) => (employees === 1
    ? `1 empleado · ${hours} h de mano de obra`
    : `${employees} empleados · ${hours} h de mano de obra`),
  disputesFlag: 'en disputa',

  // ── lista de reportes diarios ─────────────────────────────────────────────────────────────────
  dailyReportsTitle: 'Reportes diarios',
  myReports: 'Mis reportes',
  needsReview: 'Falta revisar',
  allStatuses: 'Todos los estados',
  allReviewable: 'Todos por revisar',
  reportCount: (n) => (n === 1 ? '1 reporte' : `${n} reportes`),
  noReportsFound: 'No se encontraron reportes',
  createFirstReport: 'Cree su primer reporte diario',
  workerCount: (n) => (n === 1 ? '1 trabajador' : `${n} trabajadores`),
  // `ticket` stays English — see the header.
  ticketCount: (n) => (n === 1 ? '1 ticket' : `${n} tickets`),
  disputeCount: (n) => (n === 1 ? '1 disputa' : `${n} disputas`),

  // ── la cola de disputas del revisor ─────────────────────────────────────────────────────
  disputesTab: 'Disputas',
  disputesOpen: (n) => `Abiertas (${n})`,
  disputesResolved: (n) => `Resueltas (${n})`,
  noOpenDisputes: 'Nadie ha disputado sus horas.',
  noResolvedDisputes: 'Todavia no se ha resuelto ninguna disputa.',
  disputesForbidden: 'No tiene permiso para revisar horas en disputa, por lo que esta lista no se cargo. No esta vacia.',
  disputesUnreadable: 'No se pudo leer la lista de disputas. No esta vacia; intente de nuevo.',
  disputeStateOpen: 'Abierta',
  disputeStateResolved: 'Resuelta',
  disputeHoursRecorded: (h) => `${Number(h).toFixed(1)} h registradas`,
  disputeHoursChanged: (was, now) =>
    `${Number(was).toFixed(1)} h registradas, ahora ${Number(now).toFixed(1)} h`,
  disputeResolutionNote: 'Resolucion',
  noProject: 'Sin proyecto',
  disputedBlocks: (n) => (n === 1
    ? '1 bloque de tiempo en disputa'
    : `${n} bloques de tiempo en disputa`),
  prev: 'Anterior',
  next: 'Siguiente',
  pageOf: (page, total) => `Página ${page} de ${total}`,
}
